import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  FlatList,
  NativeSyntheticEvent,
  TextInput,
  TextInputChangeEventData,
  TextInputKeyPressEventData,
} from 'react-native'

import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { GoSearchModalClientHit } from './GoSearchModalClientHit'

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: spacing['24px'],
}))

const InputContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  borderColor: colors.inputOutlineDefault,
  borderWidth: '1px',
  borderRadius: '8px',
  padding: `${spacing['12px']} ${spacing['16px']}`,
}))

const Input = styled(TextInput)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['8px'],
  flexGrow: 1,
  outlineWidth: '0',
  fontSize: '16px',
}))

const ClientList = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  borderColor: colors.borderDefault,
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderBottomWidth: '1px',
  maxHeight: '500px',
}))

interface GoSearchBarModalProps {
  isSearchBarOpen: boolean
  onHitClick: (hit: ClientListClientObject) => void
  onRequestClose: () => void
  onSearch: (event: NativeSyntheticEvent<TextInputChangeEventData>) => ClientListClientObject[]
  isPrivacyGuardActive: boolean
}

export const GoSearchModal: FunctionComponent<GoSearchBarModalProps> = ({
  isSearchBarOpen,
  onHitClick,
  onRequestClose,
  onSearch,
  isPrivacyGuardActive,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const [clientHits, setClientHits] = useState<ClientListClientObject[]>([])
  const [selectedHit, setSelectedHit] = useState<ClientListClientObject | null>(null)
  const flatListRef = useRef<FlatList>(null)

  useEffect(() => {
    if (isPrivacyGuardActive) {
      onRequestClose()
    }
  }, [onRequestClose, isPrivacyGuardActive])

  const onChange = useCallback(
    (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
      if (event.nativeEvent.text === '') setSelectedHit(null)
      setClientHits(onSearch(event))
    },
    [onSearch],
  )

  const updateScrollPosition = (index: number, viewPosition?: number) => {
    flatListRef.current?.scrollToIndex({
      animated: true,
      index,
      viewPosition: viewPosition || 0.5,
    })
  }

  const onKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    const key = event.nativeEvent.key

    if (!clientHits) {
      return
    }
    if (key === KEYS.ARROW_DOWN) {
      if (!selectedHit) {
        setSelectedHit(clientHits[0])
      } else {
        const selectedHitIndex = clientHits.findIndex(({ id }) => id === selectedHit.id)
        if (selectedHitIndex !== -1) {
          // When the last one is selected, go back to the top
          if (selectedHitIndex === clientHits.length - 1) {
            setSelectedHit(clientHits[0])
            updateScrollPosition(0, 0)
          } else {
            setSelectedHit(clientHits[selectedHitIndex + 1])
            updateScrollPosition(selectedHitIndex)
          }
        }
      }
    } else if (key === KEYS.ARROW_UP) {
      if (!selectedHit) {
        setSelectedHit(clientHits[clientHits.length - 1])
      } else {
        const selectedHitIndex = clientHits.findIndex(({ id }) => id === selectedHit.id)
        if (selectedHitIndex !== -1) {
          // When the first one is selected, go back to the bottom
          if (selectedHitIndex === 0) {
            setSelectedHit(clientHits[clientHits.length - 1])
            updateScrollPosition(clientHits.length - 1, 1)
          } else {
            setSelectedHit(clientHits[selectedHitIndex - 1])
            updateScrollPosition(selectedHitIndex)
          }
        }
      }
    } else if (key === KEYS.ENTER && !!selectedHit) {
      onHitClick(selectedHit)
    }
  }

  return (
    <Modal
      closeOnScrim
      disableBottomSheet
      modalContents={
        <>
          <HeaderContainer>
            <Title
              text={formatMessage({
                defaultMessage: 'Search clients',
                description: 'Modal title for global client search',
              })}
              size={TitleSize.MEDIUM}
            />
            <PressableOpacity
              onPress={onRequestClose}
              testID={tID('GoSearchModal-close-btn')}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close Button',
                description: 'This button closes the modal window',
              })}
            >
              <XIcon size={24} fillColor={colors.iconDefault} />
            </PressableOpacity>
          </HeaderContainer>

          <InputContainer>
            <SearchIcon size={24} />
            <Input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              testID={tID('GoSearchModal-input')}
              placeholder={formatMessage({
                defaultMessage: 'Search by name or Lyra ID',
                description: 'Placeholder text for client search input',
              })}
              onChange={onChange}
              onKeyPress={onKeyPress}
              placeholderTextColor={colors.textInactive}
            />
          </InputContainer>

          {!isEmpty(clientHits) && (
            <ClientList>
              <FlatList
                data={clientHits}
                renderItem={({ item }) => {
                  return (
                    <GoSearchModalClientHit
                      hit={item}
                      selected={selectedHit?.id === item.id}
                      onClick={() => onHitClick(item)}
                    />
                  )
                }}
                keyExtractor={(item) => item.id}
                ref={flatListRef}
                scrollEnabled
              />
            </ClientList>
          )}
        </>
      }
      onRequestClose={onRequestClose}
      visible={isSearchBarOpen}
    />
  )
}
